import { ReactElement, useMemo } from "react";
import { t } from "@lingui/core/macro";

import { Redirect, Route, Switch } from "_/components/router";

import { TabBar, TabForRoute } from "_/components/tab-bar";
import { testPermissions } from "_/components/permission-test";

import { useCheckCurrentOrg } from "_/data/orgs";
import { useIsAonUser } from "_/data/users";
import {
  Permission,
  ROOT_DOMAIN,
  useCurrentUserPermissions,
} from "_/data/rbac";

import { adminRoutes, adminUrls, settingsUrls } from "_/routes";

import { Machines } from "./machines";
import { Materials } from "./materials";
import { Software } from "./software";
import { Organizations } from "./organizations";
import { OrgOverview } from "./organizations/organization";
import { Buildset } from "./software/buildset";

import * as S from "./styled";

type TabInfo = {
  name: string;
  route: string;
  url: string;
  Component: () => ReactElement;
  requiredPermissions?: Permission | Permission[] | Permission[][];
};

const TopLevelTabs = ({
  tabs,
  showTabBar,
}: {
  tabs: TabInfo[];
  showTabBar: boolean;
}): ReactElement => {
  return (
    <S.Header>
      {showTabBar && (
        <S.Nav>
          <TabBar>
            {tabs.map(({ name, route, url }) => (
              <TabForRoute key={route} route={route} url={url}>
                {name}
              </TabForRoute>
            ))}
          </TabBar>
        </S.Nav>
      )}
    </S.Header>
  );
};

export const AdminView = () => {
  const isAonUser = useIsAonUser();
  const { data: rootPermissions } = useCurrentUserPermissions(ROOT_DOMAIN);
  const { data: currentOrg } = useCheckCurrentOrg();

  const currentOrgId = currentOrg?.id;

  const { routes, tabs, showTabBar } = useMemo(() => {
    const tabsInner: TabInfo[] = [
      {
        name: t`components.admin-view.organizations`,
        route: adminRoutes.wildcards.organizations,
        url: adminUrls.organizations,
        Component: Organizations,
        requiredPermissions: [["organizations/read"], ["machines/read"]],
      },
      {
        name: t`common.machines`,
        route: adminRoutes.machines,
        url: adminUrls.machines,
        Component: Machines,
        requiredPermissions: "machines/read",
      },
      {
        name: t`common.materials`,
        route: adminRoutes.materials,
        url: adminUrls.materials,
        Component: Materials,
        requiredPermissions: "materials/read",
      },
      {
        name: t`common.software`,
        route: adminRoutes.wildcards.software,
        url: adminUrls.software,
        Component: Software,
        requiredPermissions: [["buildSets/read", "channels/read"]],
      },
    ];

    // Filter out tabs that the user doesn't have permission to see
    const filteredTabs = tabsInner.filter((tab) =>
      testPermissions(tab.requiredPermissions ?? [], rootPermissions)
    );

    // If there are no tabs the user can see, don't show the tab bar
    const showTabBarInner = isAonUser && !!filteredTabs.length;

    // Redirect URL for managing the current org. If no current org is set,
    // redirect to the Organizations tab of the settings page.
    const currentOrgRedirectUrl = currentOrgId
      ? adminUrls.organization(currentOrgId)
      : settingsUrls.organizations;

    // Redirect URL for internal users. If the tab bar is displayed for this user,
    // redirect to the first accessible tab. Otherwise, use the redirect for the
    // current org defined above.
    const redirectUrl = showTabBarInner
      ? filteredTabs[0].url
      : currentOrgRedirectUrl;

    const tabRoutes = filteredTabs.map((tab) => (
      <Route key={tab.route} path={tab.route} component={tab.Component} />
    ));

    return {
      routes: !isAonUser ? (
        // Non-internal users can only see the manage page for their own org
        <Switch>
          <Route path={adminRoutes.organization} component={OrgOverview} />
          <Redirect to={currentOrgRedirectUrl} replace />
        </Switch>
      ) : (
        <Switch>
          <Route path={adminRoutes.organization} component={OrgOverview} />
          <Route path={adminRoutes.buildset} component={Buildset} />
          {tabRoutes}
          <Redirect to={redirectUrl} replace />
        </Switch>
      ),
      tabs: filteredTabs,
      showTabBar: showTabBarInner,
    };
  }, [currentOrgId, rootPermissions, isAonUser]);

  return (
    <S.Wrapper>
      <TopLevelTabs tabs={tabs} showTabBar={showTabBar} />
      {routes}
    </S.Wrapper>
  );
};
